import { useEffect, useLayoutEffect, useRef, useState, type FormEvent, type ReactNode } from 'react';
import type { PublicStream } from '@zip2live/shared';
import { CHAT_MAX_CHARS, chatLength, validateChatName, type ChatMessage, type ChatMode } from '@zip2live/shared/chat';
import {
  chatHost, loadOlder, loadWholeChat, myAuthorKeys, reportChat, saveChatName, sendChat,
  useChat, useChatIdentity, useHiddenAuthors, type ChatError, type ShownMessage,
} from '../lib/chat.ts';
import { block, useBlocks } from '../lib/safety.ts';
import { ensureAgreed, forgetAgreement, isRulesError } from '../lib/rules.ts';
import { CloseIcon, GearIcon, PinIcon, SendIcon, SmileIcon } from './Icons.tsx';
import { frameOnScreenAt } from './Player.tsx';

/** Chat is read-only once the broadcast is over. */
export const chatClosed = (s: PublicStream) => ['ENDING', 'ENDED', 'EXPIRED', 'FAILED'].includes(s.status);

// Light tints that read on navy and, with a shadow, over video.
const NAME_COLOURS = ['#ff8a91', '#7dd3fc', '#fcd34d', '#86efac', '#c4b5fd', '#f9a8d4', '#fdba74', '#5eead4'];
function nameColour(m: ChatMessage) {
  if (m.author.badge === 'host') return '#ffffff';
  let h = 0;
  for (const ch of m.author.key) h = (h * 31 + ch.charCodeAt(0)) >>> 0;
  return NAME_COLOURS[h % NAME_COLOURS.length];
}

const URL_RE = /(https?:\/\/[^\s]+|www\.[^\s]+)/gi;
function linkify(text: string): ReactNode[] {
  return text.split(URL_RE).map((part, i) =>
    i % 2 === 1 ? (
      <a key={i} href={part.startsWith('www.') ? `https://${part}` : part} target="_blank" rel="nofollow ugc noopener noreferrer" className="underline underline-offset-2" onClick={(e) => e.stopPropagation()}>
        {part}
      </a>
    ) : (
      part
    ),
  );
}

function mentions(m: ChatMessage, myName: string | null | undefined) {
  if (!myName || myAuthorKeys.has(m.author.key)) return false;
  const bare = myName.replace(/^@/, '').toLowerCase();
  return bare.length >= 2 && new RegExp(`(^|[^\\p{L}\\p{N}])@?${bare.replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}($|[^\\p{L}\\p{N}])`, 'iu').test(m.text);
}

const time = (at: number) => new Date(at).toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });

/** The stream has a replay to watch: its chat plays back in step with the video. */
const hasReplay = (s: PublicStream) => s.status === 'ENDED' && Boolean(s.playbackUrl);

/**
 * Wall-clock time of the replay frame on screen (from the playlist's program date-times), polled
 * while `on`. Messages sent up to that moment are shown, so seeking moves the chat with the video.
 */
function usePlayhead(on: boolean): number | null {
  const [at, setAt] = useState<number | null>(null);
  useEffect(() => {
    if (!on) return;
    const tick = () => setAt(frameOnScreenAt());
    tick();
    const t = setInterval(tick, 250);
    return () => clearInterval(t);
  }, [on]);
  return on ? at : null;
}

function Badge({ m }: { m: ChatMessage }) {
  if (m.author.badge === 'host') return <span className="speed-text mr-1.5 inline-block bg-race px-1.5 align-[1px] text-[10px] leading-4 text-white">HOST</span>;
  if (m.author.badge === 'mod') return <span title="Moderator" className="speed-text mr-1.5 inline-block bg-white px-1.5 align-[1px] text-[10px] leading-4 text-navy">MOD</span>;
  if (m.author.badge === 'verified') {
    return (
      <span title="Verified account" aria-label="Verified" className="mr-1 inline-flex h-3.5 w-3.5 items-center justify-center rounded-full bg-white align-[-1px] text-[9px] font-black text-navy">
        ✓
      </span>
    );
  }
  return null;
}

/** One line as it appears over video (overlay) or in the panel. */
function Line({ m, big = false }: { m: ShownMessage; big?: boolean }) {
  return (
    <>
      <Badge m={m} />
      <span className="font-extrabold" style={{ color: nameColour(m) }}>
        {m.author.name}
      </span>
      <span className={big ? 'ml-2' : 'ml-1.5'}>{linkify(m.text)}</span>
    </>
  );
}

const REPORT_REASONS: [string, string][] = [['spam', 'Spam'], ['harassment', 'Harassment'], ['hate', 'Hate'], ['sexual', 'Sexual'], ['violence', 'Violence'], ['other', 'Other']];

/**
 * The full chat: pinned message, messages with actions, and the box to write in.
 * `host` is decided by the server (the stream's owner); host tools appear only then.
 */
export function ChatPanel({ stream, className = '', onClose, autoFocus = false }: { stream: PublicStream; className?: string; onClose?: () => void; autoFocus?: boolean }) {
  const chat = useChat(stream.id);
  const who = useChatIdentity(stream.id);
  // People you blocked: their messages are left out (lib/chat.ts useHiddenAuthors).
  useBlocks(stream.id);
  const host = Boolean(who?.host);
  // Host or mod: the chat tools. Only the host manages mods.
  const moderator = host || Boolean(who?.mod);
  const closed = chatClosed(stream);
  const [settingsOpen, setSettingsOpen] = useState(false);
  const [replyTo, setReplyTo] = useState<ChatMessage | null>(null);
  const replay = hasReplay(stream);
  const [showAll, setShowAll] = useState(false);
  const sync = replay && !showAll;
  const playhead = usePlayhead(sync);
  useEffect(() => {
    if (replay) void loadWholeChat(stream.id);
  }, [replay, stream.id]);
  // Outcome of a host action whose message is gone (deleted, timed out, banned).
  const [notice, setNotice] = useState<string | null>(null);
  useEffect(() => {
    if (!notice) return;
    const t = setTimeout(() => setNotice(null), 5000);
    return () => clearTimeout(t);
  }, [notice]);
  const pinned = chat.settings.pinned;
  const slow = chat.settings.slowSeconds;

  return (
    <section aria-label="Live chat" className={`flex min-h-0 flex-col overflow-hidden bg-navy text-white ${className}`}>
      <header className="flex shrink-0 items-center justify-between gap-2 border-b border-white/10 py-2.5 pr-2 pl-4">
        <div className="flex min-w-0 items-center gap-2">
          <h2 className="speed-text text-[15px]">{closed ? 'Chat replay' : 'Live chat'}</h2>
          {!chat.connected && chat.ready && !closed && <span className="text-xs font-semibold text-faint-dark">Reconnecting…</span>}
          {slow > 0 && !closed && <span className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-bold">Slow {slow} s</span>}
          {chat.settings.mode === 'verified' && !closed && <span className="rounded-full bg-white/10 px-2 py-0.5 text-[11px] font-bold">Verified only</span>}
        </div>
        <div className="flex items-center">
          {moderator && !closed && (
            <button type="button" aria-label="Chat settings" aria-expanded={settingsOpen} onClick={() => setSettingsOpen((o) => !o)} className={`inline-flex h-9 w-9 items-center justify-center rounded-full ${settingsOpen ? 'bg-white text-navy' : 'hover:bg-white/10'}`}>
              <GearIcon />
            </button>
          )}
          {onClose && (
            <button type="button" aria-label="Close chat" onClick={onClose} className="inline-flex h-9 w-9 items-center justify-center rounded-full hover:bg-white/10">
              <CloseIcon />
            </button>
          )}
        </div>
      </header>
      {moderator && settingsOpen && !closed && <HostSettings streamId={stream.id} mode={chat.settings.mode} slow={slow} />}
      {pinned && (
        <div className="flex shrink-0 items-start gap-2 border-b border-white/10 bg-white/5 px-4 py-2.5 text-[14px] leading-snug">
          <span className="mt-0.5 text-race-300" aria-label="Pinned">
            <PinIcon />
          </span>
          <p className="min-w-0 flex-1 break-words">
            <Line m={pinned} />
          </p>
          {moderator && (
            <button type="button" aria-label="Unpin" className="-mt-1 -mr-1 inline-flex h-7 w-7 shrink-0 items-center justify-center rounded-full text-white/70 hover:bg-white/10" onClick={() => void chatHost.pin(stream.id, null)}>
              <CloseIcon />
            </button>
          )}
        </div>
      )}
      <Messages stream={stream} host={host} moderator={moderator} myName={who?.name} onReply={(m) => setReplyTo(m)} onNotice={setNotice} until={sync ? playhead : undefined} />
      {notice && (
        <p role="status" className="shrink-0 border-t border-white/10 bg-white/10 px-4 py-2 text-sm font-semibold">
          {notice}
        </p>
      )}
      {closed && replay ? (
        <p className="flex shrink-0 items-center justify-between gap-3 border-t border-white/10 px-4 py-3 text-sm text-muted-dark">
          {sync ? 'Chat plays along with the video.' : 'Showing every message.'}
          <button type="button" className="shrink-0 font-bold text-white underline underline-offset-2" onClick={() => setShowAll((v) => !v)}>
            {sync ? 'Show all' : 'Play along'}
          </button>
        </p>
      ) : closed ? (
        <p className="shrink-0 border-t border-white/10 px-4 py-3.5 text-sm text-muted-dark">Chat is closed. These messages were sent during the broadcast.</p>
      ) : chat.settings.mode === 'off' && !moderator ? (
        <p className="shrink-0 border-t border-white/10 px-4 py-3.5 text-sm text-muted-dark">The host turned chat off.</p>
      ) : (
        <Composer stream={stream} replyTo={replyTo} onClearReply={() => setReplyTo(null)} autoFocus={autoFocus} />
      )}
    </section>
  );
}

function Segmented<T extends string | number>({ label, value, options, onChange }: { label: string; value: T; options: [T, string][]; onChange: (v: T) => void }) {
  return (
    <div role="radiogroup" aria-label={label} className="mt-1.5 grid auto-cols-fr grid-flow-col gap-1 rounded-xl bg-navy-900 p-1">
      {options.map(([v, text]) => (
        <button key={String(v)} type="button" role="radio" aria-checked={v === value} onClick={() => onChange(v)} className={`h-9 rounded-lg text-sm font-bold ${v === value ? 'bg-white text-navy' : 'text-white/80 hover:bg-white/10'}`}>
          {text}
        </button>
      ))}
    </div>
  );
}

function HostSettings({ streamId, mode, slow }: { streamId: string; mode: ChatMode; slow: number }) {
  const [error, setError] = useState<string | null>(null);
  const change = (c: { mode?: ChatMode; slowSeconds?: number }) => chatHost.settings(streamId, c).then(() => setError(null), (e: Error) => setError(e.message));
  return (
    <div className="shrink-0 border-b border-white/10 px-4 py-3">
      <p className="text-xs font-extrabold tracking-[0.1em] text-faint-dark uppercase">Slow mode</p>
      <Segmented label="Slow mode" value={slow} options={[[0, 'Off'], [5, '5 s'], [30, '30 s']]} onChange={(v) => void change({ slowSeconds: v })} />
      <p className="mt-3 text-xs font-extrabold tracking-[0.1em] text-faint-dark uppercase">Who can chat</p>
      <Segmented label="Who can chat" value={mode} options={[['everyone', 'Everyone'], ['verified', 'Verified'], ['off', 'Nobody']]} onChange={(v) => void change({ mode: v })} />
      {error && <p role="alert" className="mt-2 text-sm text-race-300">{error}</p>}
    </div>
  );
}

function Messages({ stream, host, moderator, myName, onReply, onNotice, until }: {
  stream: PublicStream; host: boolean; moderator: boolean; myName: string | null | undefined; onReply: (m: ChatMessage) => void; onNotice: (text: string) => void;
  /** Replay: only messages sent up to this moment (null: the video hasn't started). */
  until?: number | null;
}) {
  const chat = useChat(stream.id);
  const hidden = useHiddenAuthors();
  const list = chat.messages.filter((m) => !hidden.has(m.author.key) && (until === undefined || (until !== null && m.at <= until)));
  const scroller = useRef<HTMLDivElement>(null);
  const stick = useRef(true);
  const [unseen, setUnseen] = useState(0);
  const [selected, setSelected] = useState<number | null>(null);
  const [loadingOlder, setLoadingOlder] = useState(false);
  const lastSeq = list.at(-1)?.seq;
  const firstSeq = list[0]?.seq;
  const prevFirst = useRef<{ seq?: number; height: number }>({ height: 0 });

  // Follow new messages while you're at the bottom; otherwise count them on a "new messages" pill.
  useLayoutEffect(() => {
    const el = scroller.current;
    if (!el) return;
    if (prevFirst.current.seq !== undefined && firstSeq !== undefined && firstSeq < prevFirst.current.seq) {
      // Older messages were added on top: keep what you were reading in place.
      el.scrollTop += el.scrollHeight - prevFirst.current.height;
    } else if (stick.current) {
      el.scrollTop = el.scrollHeight;
    } else {
      setUnseen((n) => n + 1);
    }
    prevFirst.current = { seq: firstSeq, height: el.scrollHeight };
  }, [lastSeq, firstSeq, list.length]);

  const onScroll = () => {
    const el = scroller.current!;
    stick.current = el.scrollHeight - el.scrollTop - el.clientHeight < 48;
    if (stick.current) setUnseen(0);
    prevFirst.current.height = el.scrollHeight;
  };
  const toBottom = () => {
    const el = scroller.current!;
    el.scrollTop = el.scrollHeight;
    stick.current = true;
    setUnseen(0);
  };

  const older = async () => {
    setLoadingOlder(true);
    await loadOlder(stream.id).catch(() => undefined);
    setLoadingOlder(false);
  };

  const waiting = ['CREATED', 'PREPARING', 'READY', 'CONNECTING'].includes(stream.status);
  return (
    <div className="relative min-h-0 flex-1">
      <div ref={scroller} onScroll={onScroll} className="h-full overflow-y-auto overscroll-contain px-2 py-2" aria-live="polite" aria-relevant="additions">
        {chat.hasOlder && (
          <button type="button" disabled={loadingOlder} onClick={() => void older()} className="mx-auto mb-2 block rounded-full bg-white/10 px-3.5 py-1.5 text-xs font-bold hover:bg-white/15 disabled:opacity-50">
            {loadingOlder ? 'Loading…' : 'Load earlier messages'}
          </button>
        )}
        {!chat.ready && <p className="px-2 py-6 text-center text-sm text-faint-dark">Connecting to chat…</p>}
        {chat.ready && list.length === 0 && (
          <p className="px-4 py-8 text-center text-sm text-faint-dark">
            {until !== undefined && chat.messages.length ? 'Messages appear as the replay plays.' : chatClosed(stream) ? 'Nobody chatted during this broadcast.' : waiting ? 'Chat while you wait. Messages show up here.' : 'No messages yet. Say hi!'}
          </p>
        )}
        <ul className="flex flex-col">
          {list.map((m) => (
            <MessageRow
              key={m.pending ? m.clientId : m.seq}
              m={m}
              stream={stream}
              host={host}
              moderator={moderator}
              mention={mentions(m, myName)}
              selected={selected === m.seq}
              onSelect={() => setSelected((s) => (s === m.seq || m.pending ? null : m.seq))}
              onReply={() => {
                onReply(m);
                setSelected(null);
              }}
              onNotice={onNotice}
            />
          ))}
        </ul>
      </div>
      {unseen > 0 && (
        <button type="button" onClick={toBottom} className="absolute bottom-3 left-1/2 -translate-x-1/2 rounded-full bg-white px-4 py-2 text-sm font-extrabold text-navy shadow-lg">
          ↓ New messages
        </button>
      )}
    </div>
  );
}

function MessageRow({ m, stream, host, moderator, mention, selected, onSelect, onReply, onNotice }: {
  m: ShownMessage; stream: PublicStream; host: boolean; moderator: boolean; mention: boolean; selected: boolean; onSelect: () => void; onReply: () => void; onNotice: (text: string) => void;
}) {
  const [step, setStep] = useState<'actions' | 'report' | 'ban' | 'block'>('actions');
  const [note, setNote] = useState<string | null>(null);
  const mine = myAuthorKeys.has(m.author.key) || m.pending;
  const isHostLine = m.author.badge === 'host';
  // Mods act on viewers; the host acts on everyone (the server checks this too).
  const canActOn = moderator && !isHostLine && !mine && (host || m.author.badge !== 'mod');
  const closed = chatClosed(stream);
  // The row may vanish with the action (delete, ban), so the panel repeats the outcome.
  const act = (fn: () => Promise<unknown>, done: string, gone = false) =>
    fn().then(
      () => (gone ? onNotice(done) : setNote(done)),
      (e: Error) => setNote(e.message),
    );
  useEffect(() => {
    if (!selected) {
      setStep('actions');
      setNote(null);
    }
  }, [selected]);

  const action = (label: string, onClick: () => void, danger = false) => (
    <button type="button" onClick={onClick} className={`h-8 rounded-full px-3 text-xs font-extrabold ${danger ? 'bg-race text-white hover:bg-race-600' : 'bg-white/10 hover:bg-white/20'}`}>
      {label}
    </button>
  );

  return (
    <li className={`rounded-lg ${mention ? 'bg-race/25' : selected ? 'bg-white/10' : ''} ${m.pending ? 'opacity-60' : ''}`}>
      <div
        role="button"
        tabIndex={0}
        aria-expanded={selected}
        title={time(m.at)}
        onClick={onSelect}
        onKeyDown={(e) => (e.key === 'Enter' || e.key === ' ') && (e.preventDefault(), onSelect())}
        className={`cursor-pointer rounded-lg px-2 py-1 text-[15px] leading-snug break-words outline-none focus-visible:ring-2 focus-visible:ring-race ${selected || mention ? '' : 'hover:bg-white/5'}`}
      >
        {m.reply && (
          <p className="mb-0.5 truncate text-xs text-faint-dark">
            ↳ <span className="font-bold">{m.reply.name}</span> {m.reply.text}
          </p>
        )}
        <Line m={m} />
      </div>
      {selected && (
        <div className="flex flex-wrap items-center gap-1.5 px-2 pt-1 pb-2">
          <span className="mr-1 text-xs text-faint-dark tabular-nums">{time(m.at)}</span>
          {note ? (
            <span className="text-xs font-bold">{note}</span>
          ) : step === 'report' ? (
            <>
              <span className="mr-1 text-xs font-bold">Report for</span>
              {REPORT_REASONS.map(([value, label]) => (
                <span key={value}>{action(label, () => void act(() => reportChat(stream.id, m.seq, value), 'Reported. Thanks.'))}</span>
              ))}
            </>
          ) : step === 'block' ? (
            <>
              <span className="mr-1 text-xs font-bold">
                Block {m.author.name}? You won’t see their messages or streams{host ? ', and they can’t chat on your streams' : ''}.
              </span>
              {action('Block', () => void act(() => block(stream.id, m.seq), `${m.author.name} is blocked. Unblock them on your account page.`, true), true)}
              {action('Cancel', () => setStep('actions'))}
            </>
          ) : step === 'ban' ? (
            <>
              <span className="mr-1 text-xs font-bold">Ban {m.author.name} from all {host ? 'your' : 'the host’s'} streams?</span>
              {action('Ban', () => void act(() => chatHost.ban(stream.id, m.seq), host ? `${m.author.name} is banned from your chat. Lift it on your account page.` : `${m.author.name} is banned from this chat.`, true), true)}
              {action('Cancel', () => setStep('actions'))}
            </>
          ) : (
            <>
              {!closed && action('Reply', onReply)}
              {moderator && !closed && action('Pin', () => void act(() => chatHost.pin(stream.id, m.seq), 'Pinned.'))}
              {(host || canActOn) && action('Delete', () => void act(() => chatHost.remove(stream.id, m.seq), 'Message deleted.', true))}
              {canActOn && !closed && action('Time out 5 min', () => void act(() => chatHost.timeout(stream.id, m.seq), `${m.author.name} is timed out for 5 minutes.`, true))}
              {canActOn && action('Ban', () => setStep('ban'), true)}
              {host && m.author.badge === 'verified' && m.author.name.startsWith('@') && action('Make mod', () => void act(() => chatHost.makeMod(stream.id, m.seq), `${m.author.name} is now a mod on all your streams.`))}
              {host && m.author.badge === 'mod' && action('Remove mod', () => void act(() => chatHost.removeMod(stream.id, m.seq), `${m.author.name} is no longer a mod.`))}
              {!moderator && !mine && action('Report', () => setStep('report'))}
              {!mine && action('Block', () => setStep('block'))}
            </>
          )}
        </div>
      )}
    </li>
  );
}

const QUICK_EMOJI = ['👍', '❤️', '😂', '🔥', '👏', '😮', '🎉', '💯'];

function Composer({ stream, replyTo, onClearReply, autoFocus }: { stream: PublicStream; replyTo: ChatMessage | null; onClearReply: () => void; autoFocus: boolean }) {
  const who = useChatIdentity(stream.id);
  const chat = useChat(stream.id);
  const [text, setText] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [askName, setAskName] = useState(false);
  const [emoji, setEmoji] = useState(false);
  const input = useRef<HTMLInputElement>(null);
  const needsName = who !== null && !who.name && !who.host;
  const verifiedOnly = chat.settings.mode === 'verified' && who?.badge !== 'verified' && !who?.host && !who?.mod;

  useEffect(() => {
    if (!error) return;
    const t = setTimeout(() => setError(null), 8000);
    return () => clearTimeout(t);
  }, [error]);
  useEffect(() => {
    if (replyTo) input.current?.focus();
  }, [replyTo]);
  // Tapped the box before we knew whether you have a name: ask once we know.
  useEffect(() => {
    if (needsName && document.activeElement === input.current) setAskName(true);
  }, [needsName]);

  const send = async (e?: FormEvent) => {
    e?.preventDefault();
    const body = text.trim();
    if (!body) return;
    if (needsName) return setAskName(true);
    if (chatLength(body) > CHAT_MAX_CHARS) return setError(`Keep it to ${CHAT_MAX_CHARS} characters.`);
    // The first time: ZIP2LIVE's rules and your age.
    if (!(await ensureAgreed('chat'))) return;
    setError(null);
    setText('');
    const reply = replyTo;
    onClearReply();
    setEmoji(false);
    try {
      await sendChat(stream.id, body, { name: who?.name ?? 'You', badge: who?.badge ?? null }, reply);
    } catch (err) {
      const e2 = err as ChatError;
      setText((t) => t || body);
      if (isRulesError(e2)) forgetAgreement();
      setError(e2.message);
      if (e2.code === 'chat_name_required' || e2.code === 'chat_name_taken') setAskName(true);
    }
  };

  if (askName) {
    return (
      <NameForm
        streamId={stream.id}
        onDone={() => {
          setAskName(false);
          setError(null);
          setTimeout(() => input.current?.focus(), 0);
        }}
        onCancel={() => setAskName(false)}
      />
    );
  }

  const left = CHAT_MAX_CHARS - chatLength(text);
  return (
    <form onSubmit={(e) => void send(e)} className="shrink-0 border-t border-white/10 px-3 pt-2.5 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
      {replyTo && (
        <div className="mb-2 flex items-center gap-2 rounded-lg bg-white/10 py-1.5 pr-1 pl-3 text-xs">
          <p className="min-w-0 flex-1 truncate">
            Replying to <span className="font-extrabold">{replyTo.author.name}</span>: {replyTo.text}
          </p>
          <button type="button" aria-label="Cancel reply" onClick={onClearReply} className="inline-flex h-7 w-7 items-center justify-center rounded-full hover:bg-white/10">
            <CloseIcon />
          </button>
        </div>
      )}
      {error && (
        <p role="alert" className="mb-2 rounded-lg bg-race px-3 py-2 text-sm font-semibold">
          {error}
        </p>
      )}
      <div className="flex items-center gap-2">
        <input
          ref={input}
          value={text}
          autoFocus={autoFocus}
          onChange={(e) => setText(e.target.value)}
          onFocus={() => needsName && setAskName(true)}
          disabled={verifiedOnly}
          enterKeyHint="send"
          maxLength={CHAT_MAX_CHARS * 4}
          aria-label="Message"
          placeholder={verifiedOnly ? 'Only verified accounts can chat now' : who?.host ? 'Chat as the host…' : 'Say something…'}
          className="h-11 min-w-0 flex-1 rounded-xl bg-navy-900 px-3.5 text-[16px] text-white placeholder:text-faint-dark outline-none focus:ring-2 focus:ring-race disabled:opacity-60"
        />
        <button type="button" aria-label="Emoji" aria-expanded={emoji} onClick={() => setEmoji((v) => !v)} className="hidden h-11 w-11 shrink-0 items-center justify-center rounded-xl hover:bg-white/10 sm:inline-flex">
          <SmileIcon />
        </button>
        <button type="submit" aria-label="Send" disabled={!text.trim() || verifiedOnly} className="inline-flex h-11 w-11 shrink-0 items-center justify-center rounded-xl bg-race hover:bg-race-600 disabled:opacity-40">
          <SendIcon />
        </button>
      </div>
      {emoji && (
        <div className="mt-2 flex flex-wrap gap-1">
          {QUICK_EMOJI.map((e) => (
            <button key={e} type="button" className="h-9 w-9 rounded-lg text-xl hover:bg-white/10" onClick={() => {
              setText((t) => `${t}${e}`);
              input.current?.focus();
            }}>
              {e}
            </button>
          ))}
        </div>
      )}
      <div className="mt-1.5 flex justify-between gap-2 text-xs text-faint-dark">
        <span className="truncate">
          {verifiedOnly ? (
            'The host limited chat to verified accounts.'
          ) : who?.name ? (
            <>
              Chatting as <span className="font-bold text-white">{who.name}</span>
              {who.mod && ' · mod'}
              {!who.locked && !who.host && (
                <>
                  {' · '}
                  <button type="button" className="underline underline-offset-2" onClick={() => setAskName(true)}>
                    change
                  </button>
                </>
              )}
            </>
          ) : who?.host ? (
            'You’re the host'
          ) : (
            'No signup needed. Pick a name and say hi.'
          )}
        </span>
        {left < 40 && <span className={`tabular-nums ${left < 0 ? 'font-bold text-race-300' : ''}`}>{left}</span>}
      </div>
    </form>
  );
}

function NameForm({ streamId, onDone, onCancel }: { streamId: string; onDone: () => void; onCancel: () => void }) {
  const who = useChatIdentity(streamId);
  const [name, setName] = useState(who?.name && !who.locked ? who.name : '');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const save = async (e: FormEvent) => {
    e.preventDefault();
    const check = validateChatName(name);
    if (!check.ok) return setError(check.reason);
    if (!(await ensureAgreed('chat'))) return;
    setBusy(true);
    try {
      await saveChatName(streamId, check.name);
      onDone();
    } catch (err) {
      setError((err as Error).message);
    } finally {
      setBusy(false);
    }
  };
  return (
    <form onSubmit={(e) => void save(e)} className="shrink-0 border-t border-white/10 px-3 pt-3 pb-[max(0.75rem,env(safe-area-inset-bottom))]">
      <label htmlFor="chat-name" className="speed-text text-[15px]">
        What should we call you?
      </label>
      <p className="mt-0.5 text-xs text-faint-dark">Shown next to your messages. No signup needed.</p>
      <div className="mt-2 flex gap-2">
        <input
          id="chat-name"
          autoFocus
          value={name}
          onChange={(e) => setName(e.target.value)}
          maxLength={40}
          autoComplete="nickname"
          placeholder="Your name"
          className="h-11 min-w-0 flex-1 rounded-xl bg-navy-900 px-3.5 text-[16px] text-white placeholder:text-faint-dark outline-none focus:ring-2 focus:ring-race"
        />
        <button type="submit" disabled={busy || !name.trim()} className="speed-text h-11 shrink-0 rounded-xl bg-race px-4 text-[15px] hover:bg-race-600 disabled:opacity-50">
          Join chat
        </button>
      </div>
      {error && <p role="alert" className="mt-2 text-sm font-semibold text-race-300">{error}</p>}
      <button type="button" onClick={onCancel} className="mt-1.5 text-xs text-faint-dark underline underline-offset-2">
        Not now
      </button>
    </form>
  );
}

/**
 * The last few messages floating over video or the camera (phones). Tapping opens the full chat.
 * `big` is the OBS overlay: larger type, no tap target, nothing but the messages.
 */
export function ChatOverlay({ stream, onOpen, max = 5, className = '', big = false, emptyHint, fadeAfter }: {
  stream: PublicStream; onOpen?: () => void; max?: number; className?: string; big?: boolean;
  /** Shown while nobody has chatted yet, so the host knows where messages will appear. */
  emptyHint?: string;
  /** Seconds a message stays on screen (OBS overlay). */
  fadeAfter?: number;
}) {
  const chat = useChat(stream.id);
  const hidden = useHiddenAuthors();
  // On a replay the floating chat plays along with the video too.
  const replay = hasReplay(stream);
  const playhead = usePlayhead(replay);
  useEffect(() => {
    if (replay) void loadWholeChat(stream.id);
  }, [replay, stream.id]);
  const [now, setNow] = useState(() => Date.now());
  useEffect(() => {
    if (!fadeAfter) return;
    const t = setInterval(() => setNow(Date.now()), 1000);
    return () => clearInterval(t);
  }, [fadeAfter]);
  const list = chat.messages
    .filter((m) => !hidden.has(m.author.key) && (!fadeAfter || now - m.at < fadeAfter * 1000) && (!replay || (playhead !== null && m.at <= playhead)))
    .slice(-max);
  const pinned = chat.settings.pinned;
  const content = (
    <>
      {pinned && !big && (
        <p className="mb-1.5 inline-flex max-w-full items-start gap-1.5 rounded-lg bg-black/45 px-2.5 py-1.5 text-[13px] leading-snug">
          <span className="mt-0.5 shrink-0 text-race-300">
            <PinIcon />
          </span>
          <span className="min-w-0 break-words">
            <Line m={pinned} />
          </span>
        </p>
      )}
      {!list.length && emptyHint && <span className="inline-flex rounded-full bg-black/45 px-3 py-1.5 text-[13px] font-semibold">{emptyHint}</span>}
      <ul className={`flex flex-col ${big ? 'gap-2' : 'gap-1'}`}>
        {list.map((m) => (
          <li key={m.pending ? m.clientId : m.seq} className={`break-words ${big ? 'text-[22px] leading-[1.6] font-semibold' : 'text-[14px] leading-[1.7]'}`}>
            {/* A dark pill per line keeps names and text readable over bright video. */}
            <span className={`box-decoration-clone [text-shadow:0_1px_2px_rgba(0,0,0,0.6)] ${big ? 'rounded-xl bg-navy-900/80 px-3 py-1' : 'rounded-lg bg-black/45 px-2.5 py-1'}`}>
              <Line m={m} big={big} />
            </span>
          </li>
        ))}
      </ul>
    </>
  );
  if (big) return <div className={`text-white ${className}`}>{content}</div>;
  return (
    <button
      type="button"
      onClick={onOpen}
      aria-label={list.length ? `Open chat. Latest: ${list.at(-1)!.author.name}: ${list.at(-1)!.text}` : 'Open chat'}
      className={`flex w-full flex-col items-start justify-end overflow-hidden text-left text-white ${list.length >= max ? '[mask-image:linear-gradient(to_bottom,transparent,black_30%)]' : ''} ${className}`}
    >
      {content}
    </button>
  );
}

/** Full chat in a bottom sheet (phones). */
export function ChatSheet({ stream, onClose }: { stream: PublicStream; onClose: () => void }) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => e.key === 'Escape' && onClose();
    addEventListener('keydown', onKey);
    return () => removeEventListener('keydown', onKey);
  }, [onClose]);
  return (
    <div className="fixed inset-0 z-50 flex flex-col justify-end bg-black/40" role="dialog" aria-modal="true" aria-label="Live chat" onClick={onClose}>
      <div className="flex h-[72dvh] flex-col overflow-hidden rounded-t-[28px] bg-navy" onClick={(e) => e.stopPropagation()}>
        <div className="mx-auto mt-2 h-1.5 w-10 shrink-0 rounded-full bg-white/25" aria-hidden />
        <ChatPanel stream={stream} onClose={onClose} className="flex-1" />
      </div>
    </div>
  );
}
